import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { calculateWizardCompletion } from '../completionScore';
import CompletionBar from '../CompletionBar';
import BarreEtape from '../BarreEtape';

const API = process.env.REACT_APP_BACKEND_URL;

// lot CJ — une photo de 4 Mo dans le corps du POST faisait casser la requête (page d'erreur
// Cloudflare illisible) ET le bien était déjà créé : brouillon sans photo, puis doublon quand
// l'utilisateur recommençait. Les photos partent donc une par une sur `upload-media`, le même
// chemin que l'onglet Médias de la fiche, APRÈS la création du bien.
const dataUriVersFichier = (photo) => {
  const uri = photo?.url || '';
  const separateur = uri.indexOf(',');
  if (!uri.startsWith('data:') || separateur < 0) return null;
  const entete = uri.slice(5, separateur);
  const type = entete.split(';')[0] || photo.type || 'image/jpeg';
  const binaire = atob(uri.slice(separateur + 1));
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return new File([octets], photo.name || 'photo.jpg', { type });
};

// Message de refus du serveur, jamais une page HTML d'intermédiaire.
const messageRefus = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || detail.error || '';
  return '';
};

const ERREUR_CONNEXION = 'La connexion au serveur a été interrompue. Vérifiez si le bien a été '
  + 'créé dans la liste avant de recommencer.';

const Step6_Summary = ({ data, prevStep, clearDraft }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPropertyId, setCreatedPropertyId] = useState('');
  // lot AB1 — ce que le serveur a fait du propriétaire (rattaché / créé). Jamais muet.
  const [ownerResolution, setOwnerResolution] = useState(null);
  const [negociateurs, setNegociateurs] = useState([]);
  // lot CJ — envoi des photos après la création, une par une.
  const [envoiPhotos, setEnvoiPhotos] = useState(null);   // { fait, total }
  const [photosRefusees, setPhotosRefusees] = useState([]);
  const [mandatExistant, setMandatExistant] = useState(null);

  // iter122 — le récapitulatif affichait l'identifiant technique du négociateur.
  // On résout le nom lisible depuis la même source que l'étape 5.
  useEffect(() => {
    if (!data.negociateur) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/api/users/negotiators`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setNegociateurs(r.data || []))
      .catch(() => setNegociateurs([]));
  }, [data.negociateur]);

  const formatNegociateur = () => {
    if (!data.negociateur) return '-';
    if (String(data.negociateur).includes(' ')) return data.negociateur;
    const nego = negociateurs.find((n) => n.id === data.negociateur);
    if (!nego) return data.negociateur;
    const nom = `${(nego.nom || '').toUpperCase()} ${nego.prenom || ''}`.trim();
    return nom || nego.email || data.negociateur;
  };

  const completionScore = calculateWizardCompletion(data);
  const hasMandat = data.numero_mandat && data.numero_mandat.trim() !== '';
  const statutBien = hasMandat ? 'actif' : 'draft';

  const handleCreate = async (continueToEdit = false) => {
    setLoading(true);
    setError('');
    setMandatExistant(null);
    setPhotosRefusees([]);

    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expirée. Veuillez vous reconnecter.');
        setLoading(false);
        return;
      }

      // lot CJ — `photos` ne part PLUS dans ce corps : voir `dataUriVersFichier`.
      const { photos, ...donneesSansPhotos } = data;
      const response = await axios.post(`${API}/api/properties/wizard`, {
        ...donneesSansPhotos,
        statut_bien: statutBien,
        completion_score: completionScore
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const nouvelId = response.data.id;
      setCreatedPropertyId(nouvelId);
      const resolution = response.data.owner_resolution || null;
      setOwnerResolution(resolution);

      // Photos : une requête par fichier. Un refus n'annule ni le bien ni les autres photos.
      const aEnvoyer = (photos || []).filter((p) => p && p.url);
      const refusees = [];
      if (nouvelId && aEnvoyer.length > 0) {
        setEnvoiPhotos({ fait: 0, total: aEnvoyer.length });
        for (let i = 0; i < aEnvoyer.length; i += 1) {
          const photo = aEnvoyer[i];
          try {
            const fichier = dataUriVersFichier(photo);
            if (!fichier) throw new Error('format de photo non reconnu');
            const corps = new FormData();
            corps.append('file', fichier);
            // eslint-disable-next-line no-await-in-loop
            await axios.post(
              `${API}/api/properties/${nouvelId}/upload-media?media_type=photo`,
              corps,
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
          } catch (errPhoto) {
            refusees.push({
              nom: photo.name || 'photo',
              raison: messageRefus(errPhoto) || 'envoi impossible',
            });
          }
          setEnvoiPhotos({ fait: i + 1, total: aEnvoyer.length });
        }
        setEnvoiPhotos(null);
        setPhotosRefusees(refusees);
      }

      // Le brouillon n'est vidé qu'une fois la dernière photo traitée.
      if (typeof clearDraft === 'function') clearDraft();

      // lot AB1 — un rattachement à une fiche existante (ou un doublon écarté) doit être VU :
      // on n'enchaîne pas silencieusement sur l'édition dans ce cas.
      const doitEtreAnnonce = !!resolution && (
        resolution.depuis_doublon === true || !!resolution.doublon_id || resolution.action === 'introuvable'
      );

      // Une photo refusée doit être lue : on ne redirige pas en silence.
      if (continueToEdit && nouvelId && !doitEtreAnnonce && refusees.length === 0) {
        navigate(`/properties/${nouvelId}/edit`);
      } else {
        setShowSuccessModal(true);
      }
    } catch (err) {
      setEnvoiPhotos(null);
      // lot CJ — doublon de mandat : message nommé, avec un lien vers le bien existant.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.error === 'mandat_existant') {
        setMandatExistant(detail);
      } else if (err.response && err.response.status >= 400 && typeof detail !== 'undefined') {
        setError(messageRefus(err) || 'Erreur lors de la création');
      } else {
        // Pas de réponse JSON du serveur : coupure, délai dépassé, page HTML d'un
        // intermédiaire (Cloudflare). ⛔ On n'affiche jamais ce texte brut.
        setError(ERREUR_CONNEXION);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Récapitulatif
        </h2>
        <p className="text-gray-600">
          Vérifiez les informations avant de créer le bien.
        </p>
      </div>

      {/* Score de complétion — composant partagé avec les autres étapes */}
      <CompletionBar data={data} currentStep={6} />

      {/* Statut du bien */}
      <div className="bg-white border-2 border-dashed rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Statut du bien</h3>
            <p className="text-sm text-gray-600 mt-1">
              {hasMandat
                ? 'Le bien sera immédiatement actif et diffusable'
                : 'Le bien sera enregistré en brouillon (ajoutez un numéro de mandat pour l\'activer)'}
            </p>
          </div>
          {hasMandat ? (
            <span className="px-4 py-2 bg-green-100 text-green-800 rounded-full font-semibold inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              ACTIF
            </span>
          ) : (
            <span className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full font-semibold inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              BROUILLON
            </span>
          )}
        </div>
      </div>

      {/* Résumé des données */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Classification */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <i className="bi bi-tag text-blue-600"></i>
            Classification
          </h4>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-600">Opération:</span> <span className="font-medium">{data.operation}</span></div>
            <div><span className="text-gray-600">Objet:</span> <span className="font-medium">{data.objet}</span></div>
            <div><span className="text-gray-600">Profil:</span> <span className="font-medium">{data.profil}</span></div>
            <div><span className="text-gray-600">Sous-profil:</span> <span className="font-medium">{data.sous_profil}</span></div>
            {data.cible && (
              <div><span className="text-gray-600">Cible:</span> <span className="font-medium">{data.cible}</span></div>
            )}
          </div>
        </div>

        {/* Pack */}
        {data.is_pack && (
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <i className="bi bi-box-seam text-orange-600"></i>
              Pack
            </h4>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-600">Type:</span> <span className="font-medium">{data.pack_type}</span></div>
              <div><span className="text-gray-600">Mode:</span> <span className="font-medium">{data.bundle_mode}</span></div>
              <div className="text-xs text-orange-700 mt-2 inline-flex items-center gap-1.5">
                <i className="bi bi-boxes"></i>
                2 biens seront créés et liés
              </div>
            </div>
          </div>
        )}

        {/* Localisation */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <i className="bi bi-geo-alt text-green-600"></i>
            Localisation
          </h4>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-600">Ville:</span> <span className="font-medium">{data.ville}</span></div>
            <div><span className="text-gray-600">Code postal:</span> <span className="font-medium">{data.code_postal}</span></div>
            {data.secteur && <div><span className="text-gray-600">Secteur:</span> <span className="font-medium">{data.secteur}</span></div>}
          </div>
        </div>

        {/* Prix & Surface */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <i className="bi bi-currency-euro text-slate-600"></i>
            Prix & Surface
          </h4>
          <div className="space-y-2 text-sm">
            {data.prix && <div><span className="text-gray-600">Prix:</span> <span className="font-medium">{parseInt(data.prix).toLocaleString()} €</span></div>}
            {data.loyer && <div><span className="text-gray-600">Loyer:</span> <span className="font-medium">{parseInt(data.loyer).toLocaleString()} €/mois</span></div>}
            {data.droit_entree && <div><span className="text-gray-600">Pas-de-porte:</span> <span className="font-medium">{parseInt(data.droit_entree).toLocaleString()} €</span></div>}
            {data.loyer_mensuel && <div><span className="text-gray-600">Loyer mensuel HT:</span> <span className="font-medium">{parseInt(data.loyer_mensuel).toLocaleString()} €/mois</span></div>}
            <div><span className="text-gray-600">Surface:</span> <span className="font-medium">{data.surface_totale_m2} m²</span></div>
            {data.charges && <div><span className="text-gray-600">Charges:</span> <span className="font-medium">{data.charges} €</span></div>}
          </div>
        </div>

        {/* Mandat */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <i className="bi bi-file-earmark-text text-red-600"></i>
            Mandat
          </h4>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-600">N° Mandat:</span> <span className="font-medium">{data.numero_mandat || 'Non renseigné'}</span></div>
            <div><span className="text-gray-600">Date:</span> <span className="font-medium">{data.date_mandat || '-'}</span></div>
            <div><span className="text-gray-600">Négociateur:</span> <span className="font-medium">{formatNegociateur()}</span></div>
          </div>
        </div>

        {/* Médias */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <i className="bi bi-images text-indigo-600"></i>
            Médias
          </h4>
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-600">Photos:</span> <span className="font-medium">{data.photos.length}</span></div>
            <div><span className="text-gray-600">Documents:</span> <span className="font-medium">{data.documents.length}</span></div>
          </div>
        </div>
      </div>

      {/* lot CJ — mandat déjà utilisé : on nomme le bien et on propose de l'ouvrir */}
      {mandatExistant && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="wizard-mandat-existant">
          <div className="flex items-start gap-2">
            <i className="bi bi-exclamation-triangle text-amber-600"></i>
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">
                Le mandat {mandatExistant.numero_mandat} existe déjà
                {mandatExistant.reference ? ` sur ${mandatExistant.reference}` : ''}
              </p>
              <p>Aucun bien n&apos;a été créé. Modifiez le numéro de mandat à l&apos;étape 5, ou
                ouvrez le bien existant.</p>
              {mandatExistant.id && (
                <button
                  type="button"
                  onClick={() => navigate(`/properties/${mandatExistant.id}/view`)}
                  data-testid="wizard-mandat-existant-lien"
                  className="mt-2 underline font-medium"
                >
                  Ouvrir {mandatExistant.reference || 'le bien existant'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* lot CJ — progression de l'envoi des photos, une par une */}
      {envoiPhotos && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="wizard-envoi-photos">
          <p className="text-sm text-blue-900 font-semibold">
            Envoi des photos : {envoiPhotos.fait} / {envoiPhotos.total}
          </p>
          <p className="text-xs text-blue-800 mt-1">
            Le bien est créé. Ne fermez pas cette page pendant l&apos;envoi.
          </p>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <i className="bi bi-exclamation-triangle text-red-600"></i>
            <div className="text-sm text-red-800">
              <p className="font-semibold mb-1">Erreur lors de la création</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <BarreEtape classeOrdinateur="md:pt-6 md:border-t">
        <button
          onClick={prevStep}
          disabled={loading}
          aria-label="Retour"
          className="h-11 w-11 md:w-auto md:h-auto px-0 md:px-6 md:py-3 shrink-0 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <i className="bi bi-arrow-left"></i>
          <span className="hidden md:inline">Retour</span>
        </button>
        <div className="flex gap-2 md:gap-3 flex-1 md:flex-none justify-end">
          <button
            onClick={() => handleCreate(false)}
            disabled={loading}
            className="h-12 md:h-auto flex-1 md:flex-none px-4 md:px-6 md:py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="bi bi-arrow-repeat animate-spin"></i>
                Création...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Créer le bien
              </>
            )}
          </button>
          <button
            onClick={() => handleCreate(true)}
            disabled={loading}
            className="h-12 md:h-auto flex-1 md:flex-none px-4 md:px-6 md:py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <i className="bi bi-pencil-square"></i>
            Créer et éditer
          </button>
        </div>
      </BarreEtape>

      {/* Modal de succès */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <i className="bi bi-check-lg text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Bien créé avec succès !
              </h3>
              <p className="text-gray-600 mb-6">
                Le bien a été enregistré en {hasMandat ? 'mode actif' : 'brouillon'}.
              </p>

              {ownerResolution && (
                <div
                  className={`text-left text-sm rounded-lg border p-3 mb-6 ${
                    ownerResolution.action === 'rattache' && ownerResolution.depuis_doublon
                      ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                  }`}
                  data-testid="wizard-owner-resolution"
                >
                  <div className="font-semibold mb-1">
                    {ownerResolution.action === 'rattache'
                      ? 'Propriétaire : rattaché à une fiche existante'
                      : 'Propriétaire : nouvelle fiche créée'}
                  </div>
                  <div>{ownerResolution.message}</div>
                  {ownerResolution.contact_id && (
                    <button
                      onClick={() => navigate(`/contacts/${ownerResolution.contact_id}`)}
                      data-testid="wizard-owner-resolution-link"
                      className="mt-2 underline font-medium"
                    >
                      Ouvrir la fiche « {ownerResolution.contact_nom} »
                    </button>
                  )}
                </div>
              )}
              <div className="space-y-3">
                {/* lot CJ — une photo refusée n'annule ni le bien ni les autres photos */}
                {photosRefusees.length > 0 && (
                  <div className="p-4 rounded-lg border bg-amber-50 border-amber-300 text-left text-sm text-amber-900"
                       data-testid="wizard-photos-refusees">
                    <div className="font-semibold mb-1">
                      Le bien est créé. {photosRefusees.length}{' '}
                      {photosRefusees.length > 1 ? 'photos n\u2019ont pas pu être enregistrées'
                        : 'photo n\u2019a pas pu être enregistrée'}
                      {' '}({photosRefusees.map((p) => `${p.nom}, ${p.raison}`).join(' ; ')}) :
                      ajoutez-{photosRefusees.length > 1 ? 'les' : 'la'} depuis l&apos;onglet
                      Médias de la fiche.
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/properties/${createdPropertyId}/edit?tab=medias`)}
                      data-testid="wizard-photos-refusees-lien"
                      className="mt-2 underline font-medium"
                    >
                      Ouvrir l&apos;onglet Médias
                    </button>
                  </div>
                )}
                <button
                  onClick={() => navigate(`/properties/${createdPropertyId}/edit`)}
                  className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <i className="bi bi-pencil-square"></i>
                  Éditer en mode avancé
                </button>
                <button
                  onClick={() => window.location.href = '/properties/wizard/new'}
                  className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <i className="bi bi-plus-lg"></i>
                  Ajouter un autre bien
                </button>
                <button
                  onClick={() => navigate('/properties')}
                  className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  Voir la liste des biens
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step6_Summary;
